import { Component, createRef, RefObject } from 'react'
import cn from 'clsx'

// Components
import Bars from 'components/kit/waveform/bars'
import CurrentTimestamp from 'components/kit/waveform/currenttimestamp'
import Hoverline from 'components/kit/waveform/hoverline'
import LoopBlock from 'components/kit/waveform/loopblock'
import PreviewLimit from 'components/kit/waveform/previewlimit'
import Timestamp from 'components/kit/waveform/timestamp'
import Wave from 'components/kit/waveform/wave'

// Hooks
import { useMaxWidth } from '@tracklib/kit'

// Types
import { Loop } from 'types'

// Utils
import { PREVIEW_LIMIT } from '@constants'
import { timeToBarIndex } from 'modules/audio/utilities'
import { getTimeStamp } from 'components/kit/waveform/utils'

// Constants
import { WIDTH, HEIGHT, NORMALIZE } from 'components/kit/waveform/constants'

import styles from './waveform.css'

type WaveFormProps = {
  trackId: number
  duration: number
  currentPos: number
  points: Array<number>
  isActive?: boolean
  isHovered?: boolean
  onClick: (ratio: number) => unknown
  loop?: Loop | null
  useTrackPreview?: boolean
  onClickPreview: () => unknown
  barPositions?: Array<number> | null
  beatsPerBar?: number
  isHotkeySeeking: boolean
  setIsHotkeySeeking?: (isSeeking: boolean) => void
  className?: string
  showOnSmall?: boolean // eslint-disable-line
}

const calcPoint = (index: number, totalNumber: number, point: number, bottomPoints: boolean) => {
  const x = index * +(WIDTH / totalNumber).toFixed(1)
  const y = (1 + point * NORMALIZE * (bottomPoints ? 1 : -1)) * 0.5 * HEIGHT
  return `${x},${y.toFixed(1)}`
}

const createWaveformPoints = (points: Array<number>) => {
  if (!points) {
    return []
  }
  const halfHeight = HEIGHT / 2
  const topPoints = points.map((point, index) => calcPoint(index, points.length, point, false))
  const bottomPoints = points.map((point, index) => calcPoint(index, points.length, point, true)).reverse()

  return [`0,${halfHeight}`, ...topPoints, `${WIDTH},${halfHeight}`, ...bottomPoints]
}
type WaveFormState = {
  waveformPoints: Array<string>
  hoverLineX: number | null | undefined
  hoverLineXRatio: number
}

class RealWaveForm extends Component<WaveFormProps, WaveFormState> {
  ref1: RefObject<HTMLElement>
  ref2: RefObject<HTMLElement>

  constructor(props: WaveFormProps) {
    super(props)
    this.ref1 = createRef()
    this.ref2 = createRef()
  }

  state = {
    waveformPoints: createWaveformPoints(this.props.points),
    hoverLineX: null,
    hoverLineXRatio: 0,
  }

  getSnapshotBeforeUpdate(prevProps: WaveFormProps) {
    const { trackId, points } = this.props
    if (trackId !== prevProps.trackId) {
      this.setState({ waveformPoints: createWaveformPoints(points) })
    }
    return null
  }

  shouldComponentUpdate(nextProps: WaveFormProps) {
    const { isActive, isHovered, useTrackPreview, loop } = this.props
    return (
      isActive ||
      nextProps.isActive ||
      isHovered ||
      nextProps.isHovered ||
      useTrackPreview !== nextProps.useTrackPreview ||
      nextProps.loop !== loop
    )
  }

  onMouseHover = (e: React.MouseEvent) => {
    const { clientX, target } = e
    const boundingRect = (target as HTMLElement)?.getBoundingClientRect()
    const x = clientX - (boundingRect.x || boundingRect.left)
    if (this.props.setIsHotkeySeeking) {
      this.props.setIsHotkeySeeking(false)
    }
    this.setState({ hoverLineX: x, hoverLineXRatio: x / boundingRect.width })
  }

  onMouseLeave = () => this.setState({ hoverLineX: null })

  onMouseClick = () => {
    const { duration, onClick, useTrackPreview, onClickPreview } = this.props
    const { hoverLineXRatio } = this.state
    if (!useTrackPreview || hoverLineXRatio < PREVIEW_LIMIT / duration) {
      onClick(hoverLineXRatio)
    } else {
      onClickPreview()
    }
  }

  render() {
    const {
      trackId,
      currentPos,
      duration,
      isActive,
      loop,
      useTrackPreview,
      isHovered,
      barPositions,
      beatsPerBar = 1,
      isHotkeySeeking,
      className,
    } = this.props
    const { waveformPoints, hoverLineX, hoverLineXRatio } = this.state
    const playedRatio = currentPos / duration || 0
    const previewBeginRatio = PREVIEW_LIMIT / duration
    const hoverDuration = hoverLineXRatio * duration
    const currentTimeStamp = getTimeStamp(currentPos)
    const hoverTimeStamp = getTimeStamp(hoverDuration)
    const shouldRenderLoop = isActive && loop && barPositions
    let time

    if (shouldRenderLoop && loop) {
      const { beat_start, beat_count, time_start, time_end } = loop
      const start = barPositions && barPositions.length ? barPositions[Math.floor(beat_start / beatsPerBar)] : time_start
      const end = barPositions && barPositions.length ? barPositions[Math.floor((beat_start + beat_count) / beatsPerBar)] : time_end
      time = {
        start,
        end,
        beat_start,
        beat_count,
      }
    }

    const ref1 = this.ref1.current && this.ref1.current.getBoundingClientRect()
    const ref2 = this.ref2.current && this.ref2.current.getBoundingClientRect()
    const isIntersecting = (ref1 && ref2 && ref2.x + ref2.width + 10 > ref1.x) || false
    return (
      <div className={cn(styles.wrapper, !isActive && isHovered && styles.wrapperHover, className)}>
        <Timestamp ref={this.ref1} time={time} totalTime={getTimeStamp(duration)} />

        {/* eslint-disable */}
        {/* For divs that are buttons */}
        <div
          className={styles.mouseInput}
          onMouseMove={this.onMouseHover}
          onMouseLeave={this.onMouseLeave}
          onClick={this.onMouseClick}
          data-testid="mouse-input"
        >
          {isActive && hoverLineX !== null && !loop && <Hoverline time={hoverTimeStamp} left={hoverLineX} />}
        </div>
        {/* eslint-enable */}

        {useTrackPreview && <PreviewLimit previewBeginRatio={previewBeginRatio} />}

        {isActive && (
          <CurrentTimestamp ref={this.ref2} playedRatio={playedRatio} timestamp={currentTimeStamp} intersecting={isIntersecting} />
        )}

        <svg viewBox={`0 0 ${WIDTH} ${HEIGHT}`} preserveAspectRatio="none" className={styles.svg}>
          <Wave points={waveformPoints} isActive={isActive} isHovered={isHovered} playedRatio={playedRatio} trackId={trackId} />
          {loop && (
            <LoopBlock
              barIndex={Math.floor(loop.beat_start / beatsPerBar)}
              barPositions={barPositions}
              beatsPerBar={beatsPerBar}
              duration={duration}
              loop={loop}
              datatesting="current_loop_block"
            />
          )}

          {loop && shouldRenderLoop && hoverLineX !== null && !isHotkeySeeking && (
            <LoopBlock
              barIndex={timeToBarIndex(hoverDuration, barPositions)}
              barPositions={barPositions}
              beatsPerBar={beatsPerBar}
              duration={duration}
              loop={loop}
            />
          )}
          {shouldRenderLoop && barPositions && <Bars positions={barPositions} duration={duration} />}
        </svg>
      </div>
    )
  }
}

const WaveForm = (props: WaveFormProps): JSX.Element | null => {
  const isSmall = useMaxWidth(479)
  return !isSmall || props.showOnSmall ? <RealWaveForm {...props} /> : null
}
export default WaveForm
export { RealWaveForm }
